// WelcomeToEliteCore.js
// ✅ Full component file — inline styles moved to CSS classes.
// Import the CSS file below (WelcomeToEliteCore.css).

import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useHistory } from "react-router-dom";
import { AuthContext } from "../../helpers/AuthContext";
import MainDashboardLeftSidebar from "./MainDashboardLeftSidear";

import { jwtDecode } from "jwt-decode";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

//import { CommentSection } from "react-comments-section";
//import "react-comments-section/dist/index.css";

import Swal from "sweetalert2";
import { formatDistanceToNow } from "date-fns";

/* MUI */
import { Button, Collapse, TextField, Tooltip, Tabs, Tab, Chip } from "@mui/material";

import PublicIcon from "@mui/icons-material/Public";
import ApartmentIcon from "@mui/icons-material/Apartment";
import MessageIcon from "@mui/icons-material/Message";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import CampaignIcon from "@mui/icons-material/Campaign";
import UpdateIcon from "@mui/icons-material/Update";
import StorefrontIcon from "@mui/icons-material/Storefront";
import WidgetsIcon from "@mui/icons-material/Widgets";
import PaletteIcon from "@mui/icons-material/Palette";
import ExtensionIcon from "@mui/icons-material/Extension";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import LockClockIcon from "@mui/icons-material/LockClock";

/* lucide */
import { MessageCircle } from "lucide-react";

/* Local */
import ReactionButtons from "../instances/connect/feed-and-activity/reaction";

/* Styles */
import "./WelcomeToEliteCore.css";

function WelcomeToEliteCore() {
  const { authState } = useContext(AuthContext);
  const history = useHistory();

  const accessToken = localStorage.getItem("accessToken");
  const defaultAvatarUrl = "/elite-core-placeholder-avatar.png";

  // Instances
  const [listOfPosts, setListOfPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nonLoginLoading, setNonLoginLoading] = useState(true);

  // Announcements state (max 3 shown)
  const [announcements, setAnnouncements] = useState([]);
  const [announcementsLoading, setAnnouncementsLoading] = useState(true);
  const [announcementsError, setAnnouncementsError] = useState("");

  // Development log (Update Log)
  const [developmentLog, setDevelopmentLog] = useState([]);
  const [developmentLogLoading, setDevelopmentLogLoading] = useState(true);
  const [developmentLogError, setDevelopmentLogError] = useState("");
  const [developmentLogPage, setDevelopmentLogPage] = useState(0);
  const developmentLogPageSize = 9;

  // PromoWorld feed state
  const [feedMode, setFeedMode] = useState("global"); // "global" | "instance"
  const [selectedInstanceId, setSelectedInstanceId] = useState("");
  const [updates, setUpdates] = useState([]);
  const [feedLoading, setFeedLoading] = useState(true);
  const [openComments, setOpenComments] = useState({});

  // Post composer
  const [statusTitle, setStatusTitle] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const [userDetails, setUserDetails] = useState(null);

  // UI: Tabs
  const [tab, setTab] = useState(0); // 0 Feed, 1 Guides, 2 Announcements, 3 Updates, 4 Marketplace

  // 🔖 Release notes
  const changelog = [
    {
      version: "0.0.10",
      date: "September 29, 2026",
      title: "Purchasing and Project Notes workflow improvements",
      details: [
        "Purchasing notes: inbound project emails now remain visible when Production only or This PO only filters are active.",
        "Project Overview notes: relative timestamps now include the exact local date and time for faster auditing.",
        "Purchasing dashboard: visible columns now expand to fill the full table width while preserving horizontal scrolling when needed.",
        "Purchasing popup and Project Overview: optional infinite scrolling loads notes inside the notes panel without requiring pagination clicks.",
      ],
    },
    {
      version: "0.0.9",
      date: "October 17, 2025",
      title: "Mega update: Production, Purchasing, Billing & Invoicing improvements",
      details: [
        "Production: End-to-end job lifecycle from scheduling to completion, including start, pause, resume, QC, complete, cancel, and reinstate.",
        "Production: Split and merge jobs with clear history so teams can track what changed and when.",
        "Production: Machine management by department with usage tracking to improve planning and capacity visibility.",
        "Production: Update process steps and materials live during production to reduce bottlenecks and rework.",
        "Production: Unified activity log blends step events, material edits, and process changes for a single source of truth.",
        "Production: Auto-fills key project and product details at scheduling to speed up set-up.",
        "Production: Smoother screens and faster filters across production views.",
        "Production navigation: New production screens and simpler navigation, including a dedicated Machines Management area.",
        "Production filters: 'In Production' now includes Created, Submitted, Confirmed, and Open for a clearer picture of work in progress.",
        "Production filters: 'Open' now shows everything except Closed.",
        "Production filters: Added an 'All stages' option for broad reviews.",
        "Production filters: Removed 'Due by today' from Date Type to simplify date searches.",
        "Proofing: Drag-and-drop uploads, designer assignment, status lifecycle, and an 'All proofs uploaded' indicator for handoffs.",
        "Proofing: More reliable uploads and clearer statuses (Draft, Sent, Approved, Changes Requested).",
        "Purchasing: One easy-to-read row per PO beneath each Sales Order, with faster scrolling and searching.",
        "Purchasing: Click behavior is consistent—PO opens the PO, Sales Order opens the Sales Order, and the project name opens the Project Overview.",
        "Purchasing: Clearer status language—Ok, Problem, Follow Up—and 'In production' aligns with real work states.",
        "Purchasing: Removed the three-dot menu for filters to keep controls simple and obvious.",
        "Purchasing: Firm rows now display in a slightly darker red for better visibility.",
        "Purchasing: The 'Firm' filter now works as expected.",
        "Purchase Orders: Clearer PO generation flow and more reliable PDF output.",
        "Documents & PDFs: Cleaner print styles, improved address formatting, and fewer page-break surprises.",
        "Documents & PDFs: Heavier files render more reliably in the viewer, with fewer interruptions.",
        "Billing: Add/Edit Bill flow refined so it's easier to capture costs accurately.",
        "Billing: Supplier picker now lets you add a supplier on the spot if it's not found—keep moving without leaving the page.",
        "Billing: Helpful progress bar and friendly summaries so teams can see if they're under-billed, on track, or over-billed at a glance.",
        "Billing: Billed margin is now based on the total of bills received versus the cart subtotal for a truer picture of profit.",
        "Billing: Easier to review and update bills as they arrive.",
        "Invoices: Multiple tax lines now display when applicable so totals are transparent.",
        "Invoices: Terms & conditions corrected and standardized across invoices.",
        "Invoices: All costs now show with two decimal places for a consistent, professional look.",
        "Invoices: Fixed recipient email selection so the right contact is used when sending.",
        "Invoices: GST (5.00%) now shows the actual calculated tax amount on every invoice.",
        "Invoices: Clearer totals and cleaner layout across generated invoices.",
        "Reports: Early Commissions report added to help track payouts.",
        "Products: Add & edit custom products.",
        "Pricing & margins: Pass-through charges (like shipping/artwork) don't inflate margin but still count toward totals.",
        "Pricing & margins: Cents-first math throughout to prevent rounding issues.",
        "Styling stability: Prevented unexpected visual resets by scoping styles to the right areas.",
        "Icons & UI polish: Corrected icon imports and small UI inconsistencies for a smoother feel.",
        "QuickBooks: Connection established throughout app.",
        "Communication: Project chat, project overview, purchasing chat, production chat are now all aligned.",
      ],
    },
    {
      version: "0.0.8",
      date: "September 15, 2025",
      title: "Multi-recipient sending, safer messaging, and Purchasing polish",
      details: [
        "Estimates & Sales Orders: select multiple recipients via dropdown on Convert Presentation, Estimate/Sales Order, and Presentation screens.",
        "Auto-send disabled: a send options panel now appears before emailing customers.",
        "Email routing: choose the client contact to email; order-rep and client-rep are CC/BCC'd automatically on Estimate/Sales Order sends.",
        "Sales Order send button now reads 'Send sales order' when Sales Order status is selected.",
        "Corrected client email targeting user: system now honours the contact's saved email.",
        "Presentations: added history panel; each version has a unique identifier (room to expand with friendlier labels).",
        "Presentation totals: shows pre-tax and after-tax amounts to avoid confusion.",
        "Product Search (Function) section removed (use the Connect tab).",
        "Projects > Introduction of cart tab",
        "File previews fixed for PDF, AI, EPS, and SVG (now reliably viewable).",
        "Decoration area pricing now displays decimals properly (Ex., 7.50 instead of 750).",
        "Other Services → Shipping tab: pricing now saves correctly; forces quantity 1; added no-tax option.",
        "Create Customer: header and primary red button now say 'Create Customer' (was 'Create lead').",
        "People → All Customers + Leads now labeled simply 'All'",
        "Comments on presentations now email assigned order-rep and client-rep.",
        "'Request a Supplier' flow added with admin approval in Operations (statuses: approved, in-review, declined).",
        "Custom Product creation simplified; editing supported; lighter initial create UI.",
        "Website sync: Elite Core custom products can be imported to the distributor website.",
        "Purchasing: infinite scrolling for continuous load.",
        "Statuses: Ok, Problem, Follow Up fixed; 'In production' status applied for created/submitted/continued work.",
        "Reporting filters: N/A/Overdue/Today/Earlier, custom date range, and Next 7 Days.",
        "Purchasing dashboard: N/A date now populates and can be changed.",
        "PO click opens the PO (no project jump).",
        "Sales Order click opens the Sales Order; project name opens Project Overview.",
      ],
    },
    {
      version: "0.0.7",
      date: "September 8, 2025",
      title: "Purchasing rename, columns & speed",
      details: [
        "Renamed 'Production' to 'Purchasing' across the app.",
        "Purchasing: movable (drag-to-reorder) columns.",
        "Purchasing: removed Overrun, Underrun, and Ship Date columns.",
        "Purchasing reports panel: development underway.",
        "Presentations: added history view.",
        "Faster Purchasing panel and general polish.",
      ],
    },
    {
      version: "0.0.6",
      date: "September 2, 2025",
      title: "Deposits, proofing, Canadian products & more",
      details: [
        "Sales > Deposits: improved guidance explaining how deposits work and how to send deposit requests to clients.",
        "Canadian products: reliably add CA items (catalog + custom) without hiccups.",
        "Shipping/Decorator mapping: map ship-from/ship-to and decorator routing for cleaner fulfillment.",
        "Proofing upload system: attach and manage proofs right where teams expect them.",
        "Custom product flow (without decoration): streamlined add → price → include in estimate/SO.",
        "Visible project flow: only show actions that are available at each step (e.g., show 'Estimate' only once generated; reveal POs after approval, etc.).",
        "My Customers tab: quick access to owned accounts and recent activity.",
        "Purchase Orders: track who approved each PO.",
        "Other Services: support Artwork and Shipping charges for accurate totals.",
      ],
    },
    {
      version: "0.0.5",
      date: "August 25, 2025",
      title: "Core-wide fixes, caching & UX polish",
      details: [
        "Sales: default margin auto-set to 40%.",
        "Fixed product deletion bug.",
        "Only customer addresses show on Estimate / Sales Order / Purchase Order when applicable.",
        "Department verbiage updated to 'Art' where applicable.",
        "Sales Order: added Request Changes workflow.",
        "Estimate / Sales Order: code & UI cleanup.",
        "Purchase Orders: show approver and allow overrides.",
        "Projects: added Event Type, Project Name, and starting stage.",
        "Suppliers: custom suppliers now display only for the current core.",
        "Presentation / Estimate / Sales Order: speed improvements via caching.",
        "Presentation: added ability to edit.",
        "Integrated new document header.",
      ],
    },
    { version: "0.0.4", date: "August 15, 2025", title: "Projects & Production dashboard updates", details: ["Added Project Name and Event Types to Projects.", "Production Dashboard: filterable search and dynamic column selection."] },
    { version: "0.0.3", date: "August 12, 2025", title: "Custom Product addition in Edit Product", details: ["Added ability to add/select Custom Products directly in the Edit Product modal.", "Improved product detail modal parity for custom items."] },
    { version: "0.0.2", date: "August 12, 2025", title: "Minor fixes & polish", details: ["General UI polish and small fixes."] },
    { version: "0.0.1", date: "August 11, 2025", title: "Initial launch", details: ["Initial Elite Core beta release."] },
  ];

  const latestVersion = changelog[0]?.version ?? "0.0.x";

  const upcoming = useMemo(
    () => ["Reporting", "Sales optimization: Better presentations, ability to add products faster, include in price, etc."],
    []
  );

  // Token -> userDetails
  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (!token) return;
    try {
      const decoded = jwtDecode(token);
      setUserDetails(decoded);
    } catch (e) {
      console.error("Invalid token:", e);
    }
  }, []);

  // Avatar helpers
  const stringToColor = (string) => {
    let hash = 0;
    for (let i = 0; i < (string || "").length; i++) {
      hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = "#";
    for (let i = 0; i < 3; i++) {
      const value = (hash >> (i * 8)) & 0xff;
      color += `00${value.toString(16)}`.slice(-2);
    }
    return color;
  };

  const generateAvatarUrl = (name) => {
    const safeName = (name || "User").trim();
    const initials = safeName
      .split(" ")
      .map((w) => w[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
    const bg = stringToColor(safeName).substring(1);
    return `https://ui-avatars.com/api/?name=${initials}&background=${bg}&color=fff&size=128`;
  };

  const getUserAvatar = (user) => {
    if (!user || !user.profilePicture) return defaultAvatarUrl;
    let profilePic = user.profilePicture;
    if (typeof profilePic === "string") {
      try {
        profilePic = JSON.parse(profilePic);
      } catch {
        if (profilePic.startsWith("uploads")) return `${process.env.REACT_APP_NODEJS_SERVER_URL}/${profilePic}`;
        return profilePic || defaultAvatarUrl;
      }
    }
    if (Array.isArray(profilePic) && profilePic.length > 0) {
      const selected = profilePic.find((p) => p.selected) || profilePic[0];
      if (selected?.url?.startsWith("uploads")) return `${process.env.REACT_APP_NODEJS_SERVER_URL}/${selected.url}`;
      return selected?.url || defaultAvatarUrl;
    }
    return defaultAvatarUrl;
  };

  const avatarUrlForCommentSection = useMemo(() => {
    if (!userDetails) return defaultAvatarUrl;
    return defaultAvatarUrl;
  }, [userDetails]);

  const toggleComments = (postId) => {
    setOpenComments((prev) => ({ ...prev, [postId]: !prev[postId] }));
  };

  // Feed fetch
  const fetchPromoWorldFeed = async (mode, instanceIdForRequest) => {
    try {
      setFeedLoading(true);
      const token = localStorage.getItem("accessToken");
      if (!token) throw new Error("No token");

      if (!instanceIdForRequest) {
        setUpdates([]);
        return;
      }

      const res = await fetch(`${process.env.REACT_APP_NODEJS_SERVER_URL}/news-feed/?instanceId=${instanceIdForRequest}`, {
        method: "GET",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();

      const parsedInstanceId = parseInt(instanceIdForRequest, 10);

      const filtered =
        mode === "global"
          ? (Array.isArray(data) ? data.filter((p) => p.instanceId == null) : [])
          : (Array.isArray(data)
              ? data.filter((p) => p.instanceId == null || parseInt(p.instanceId, 10) === parsedInstanceId)
              : []);

      setUpdates(filtered);
    } catch (e) {
      console.error("PromoWorld feed load failed:", e);
      toast.error("Couldn’t load PromoWorld feed.");
      setUpdates([]);
    } finally {
      setFeedLoading(false);
    }
  };

  const handleCreatePost = async () => {
    try {
      if (!userDetails?.role) return;

      if (!statusTitle.trim() || !newStatus.trim()) {
        return Swal.fire({
          icon: "warning",
          title: "Incomplete Details",
          text: "Enter both a title and a message.",
          heightAuto: false,
        });
      }

      if (feedMode === "instance" && !selectedInstanceId) {
        return Swal.fire({
          icon: "warning",
          title: "Pick an Instance",
          text: "Select which instance you want to post to.",
          heightAuto: false,
        });
      }

      const payload = {
        instanceId: feedMode === "global" ? null : parseInt(selectedInstanceId, 10),
        title: statusTitle.trim(),
        message: newStatus.trim(),
        role: userDetails.role,
        tag: feedMode === "global" ? "PromoWorld" : "general",
      };

      // optimistic
      const tempId = `temp-${Date.now()}`;
      const now = new Date().toISOString();
      const optimistic = {
        id: tempId,
        createdAt: now,
        title: payload.title,
        message: payload.message,
        tag: payload.tag,
        instanceId: payload.instanceId,
        user: {
          id: userDetails.id,
          first_name: userDetails.first_name,
          last_name: userDetails.last_name,
          role: userDetails.role,
          profilePicture: userDetails.profilePicture ?? null,
        },
        comments: [],
        reactions: [],
      };

      setUpdates((prev) => [optimistic, ...prev]);
      setStatusTitle("");
      setNewStatus("");

      const res = await fetch(`${process.env.REACT_APP_NODEJS_SERVER_URL}/news-feed/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error(res.statusText);
      const json = await res.json();
      const post = json?.post || json;

      setUpdates((prev) => prev.map((u) => (u.id === tempId ? { ...u, ...post } : u)));

      toast.success(feedMode === "global" ? "Posted to PromoWorld!" : "Posted to instance!");
    } catch (e) {
      console.error(e);
      toast.error("Post failed.");
      setUpdates((prev) => prev.filter((u) => !String(u.id || "").startsWith("temp-")));
    }
  };

  const handleCommentSubmit = async (data, postId) => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) throw new Error("No token");

      const commentData = { postId, comment: data.text };

      const response = await fetch(`${process.env.REACT_APP_NODEJS_SERVER_URL}/news-feed/comment/`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(commentData),
      });

      if (!response.ok) throw new Error("Network response was not ok");

      await fetchPromoWorldFeed(feedMode, selectedInstanceId || listOfPosts?.[0]?.id);
    } catch (e) {
      console.error("Error submitting comment:", e);
      toast.error("Comment failed.");
    }
  };

  // Load instances + announcements
  useEffect(() => {
    let isMounted = true;

    const fetchAll = async () => {
      if (!localStorage.getItem("accessToken")) {
        history.push("/login");
        return;
      }

      setNonLoginLoading(false);

      // Instances
      try {
        const instancesRes = await axios.get(`${process.env.REACT_APP_NODEJS_SERVER_URL}/instances/byuserId/${authState.id}`, {
          headers: { accessToken: localStorage.getItem("accessToken") },
        });

        if (!isMounted) return;

        const instances = instancesRes.data || [];
        setListOfPosts(instances);
        setLoading(false);

        const first = instances?.[0]?.id;
        if (first && !selectedInstanceId) setSelectedInstanceId(String(first));
      } catch (e) {
        console.error("Instances load failed:", e);
        if (isMounted) setLoading(false);
      }

      // Announcements
      try {
        setAnnouncementsLoading(true);
        setAnnouncementsError("");

        const annRes = await axios.get(`${process.env.REACT_APP_NODEJS_SERVER_URL}/instances/announcements`, {
          headers: { accessToken: localStorage.getItem("accessToken") },
        });

        if (!isMounted) return;
        const items = annRes.data?.items || [];
        setAnnouncements(items.slice(0, 3));
      } catch (e) {
        console.error("Announcements load failed:", e);
        if (!isMounted) return;
        setAnnouncements([]);
        setAnnouncementsError("Couldn’t load announcements.");
      } finally {
        if (isMounted) setAnnouncementsLoading(false);
      }

      // Development log
      try {
        setDevelopmentLogLoading(true);
        setDevelopmentLogError("");

        const logRes = await axios.get(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/instances/development-log?limit=30`,
          { headers: { accessToken: localStorage.getItem("accessToken") } }
        );

        if (!isMounted) return;
        const items = logRes.data?.items || [];
        setDevelopmentLog(items);
        setDevelopmentLogPage(0);
      } catch (e) {
        console.error("Development log load failed:", e);
        if (!isMounted) return;
        setDevelopmentLog([]);
        setDevelopmentLogError("Couldn’t load update log.");
      } finally {
        if (isMounted) setDevelopmentLogLoading(false);
      }
    };

    fetchAll();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState.id, history]);

  // Load feed when mode / selected instance changes
  useEffect(() => {
    const fallback = selectedInstanceId || listOfPosts?.[0]?.id;
    if (!fallback) return;
    fetchPromoWorldFeed(feedMode, fallback);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedMode, selectedInstanceId]);

  const instanceOptions = useMemo(() => {
    return (listOfPosts || []).map((i) => ({
      id: i.id,
      name: i.instanceName || i.instanceUniqueId || `Instance #${i.id}`,
    }));
  }, [listOfPosts]);

  const developmentLogPageCount = useMemo(() => {
    return Math.max(1, Math.ceil((developmentLog || []).length / developmentLogPageSize));
  }, [developmentLog, developmentLogPageSize]);

  const developmentLogPageItems = useMemo(() => {
    const start = developmentLogPage * developmentLogPageSize;
    return (developmentLog || []).slice(start, start + developmentLogPageSize);
  }, [developmentLog, developmentLogPage, developmentLogPageSize]);

  // Role guides
  const roleGuides = [
    {
      label: "Client & Order (Sales)",
      href: "https://docs.google.com/document/d/1q5LlQ_MDky6BA0MUNcoj3SAFLusqpf45GcXQfeZi-XI/edit?tab=t.0#heading=h.mlalyi196d1p",
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <path d="M7 7h10M7 11h6M7 15h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <path
            d="M6 3h12a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z"
            stroke="currentColor"
            strokeWidth="1.5"
            opacity="0.7"
          />
          <path d="M16.5 12.5 19 10l2 2-2.5 2.5-1 .3.3-1Z" fill="currentColor" opacity="0.85" />
        </svg>
      ),
    },
    {
      label: "Production",
      href: "https://docs.google.com/document/d/1uLyicwKjv24FxF8bEo6CZTd82_JCChsM_rNH-8kJAv8/edit?tab=t.0",
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <path d="M12 3v4M12 17v4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          <path d="M4.5 7.5 7.4 10.4M16.6 16.6l2.9 2.9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" opacity="0.8" />
          <path d="M19.5 7.5 16.6 10.4M7.4 16.6l-2.9 2.9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" opacity="0.8" />
          <path d="M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8Z" stroke="currentColor" strokeWidth="1.7" />
          <path d="M12 10.2V12l1.3 1.1" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" opacity="0.85" />
        </svg>
      ),
    },
    {
      label: "Finance",
      href: "https://docs.google.com/document/d/16BCiI8YvQx7SIrpzU7gSherRqyoEExxgmm6TPn2LZt4/edit?usp=sharing",
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <path d="M4 7h16M6 3h12a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z" stroke="currentColor" strokeWidth="1.6" />
          <path d="M7 17h2.5l2-6 2 4 1.2-2H17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M7 7h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" opacity="0.7" />
        </svg>
      ),
    },
    {
      label: "Shipping",
      href: "https://docs.google.com/document/d/1-USn05KWhYAvawg7F5Lqv8thA9Qx_9KfF4KYTKkZ3G8/edit?tab=t.0#heading=h.mlalyi196d1p",
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <path d="M3 8h11v9H3V8Z" stroke="currentColor" strokeWidth="1.7" />
          <path d="M14 11h4l3 3v3h-7v-6Z" stroke="currentColor" strokeWidth="1.7" />
          <path d="M7 19a1.5 1.5 0 1 0 0.01 0Z" stroke="currentColor" strokeWidth="1.7" />
          <path d="M18 19a1.5 1.5 0 1 0 0.01 0Z" stroke="currentColor" strokeWidth="1.7" />
          <path d="M6 11h5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" opacity="0.75" />
        </svg>
      ),
    },
    {
      label: "Operations",
      href: "https://docs.google.com/document/d/1SpBJymDb3bnB3ICVGw7AgSfvjxMq2QT_o-fTAhq4988/edit?usp=sharing",
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <path d="M12 2l2.2 4.5 5 .7-3.6 3.5.9 5-4.5-2.3-4.5 2.3.9-5L4.8 7.2l5-.7L12 2Z" stroke="currentColor" strokeWidth="1.6" opacity="0.9" />
          <path d="M8.8 13.8 7.5 20.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" opacity="0.7" />
          <path d="M15.2 13.8 16.5 20.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" opacity="0.7" />
        </svg>
      ),
    },
    {
      label: "Art",
      href: "https://docs.google.com/document/d/1YVlPvn5GCUZGxvYfuq6m-TE1uLy_nz9xo3KHKk62Zmc/edit?usp=sharing",
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <path d="M12 3c5 0 9 3.8 9 8.5S17 21 12 21s-9-4.8-9-9.5S7 3 12 3Z" stroke="currentColor" strokeWidth="1.6" />
          <path d="M7.2 11.2a1 1 0 1 0 .01 0ZM10 8.6a1 1 0 1 0 .01 0ZM14 8.6a1 1 0 1 0 .01 0ZM16.8 11.2a1 1 0 1 0 .01 0Z" fill="currentColor" opacity="0.8" />
          <path d="M12 12.5c1.5 0 2.5 1 2.5 2.2 0 1.8-1.6 3.3-3.6 3.3H9.8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      ),
    },
    {
      label: "Web Store",
      href: "https://docs.google.com/document/d/1RRzonq0FK1wg9jB_kB1lvVc3R13jm3xHRJIzIJRhGlI/edit?usp=sharing",
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <path d="M4 7h16l-1.5 14H5.5L4 7Z" stroke="currentColor" strokeWidth="1.6" />
          <path d="M8 10v-2a4 4 0 0 1 8 0v2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          <path d="M7 7l2-3h6l2 3" stroke="currentColor" strokeWidth="1.6" opacity="0.7" />
          <path d="M10 14h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" opacity="0.85" />
        </svg>
      ),
    },
  ];

  // Marketplace cards
  const marketplaceCards = [
    {
      title: "Add-ons",
      icon: <ExtensionIcon />,
      subtitle: "Install unique functions into PromoStores (Elite Core-built today, public later).",
      bullets: [
        "Checkout guards + restrictions",
        "Employee vouchers + access rules",
        "Approvals + review workflows",
        "Automation packs (shipping, notifications, etc.)",
      ],
      tag: "Coming soon",
    },
    {
      title: "Design Marketplace",
      icon: <PaletteIcon />,
      subtitle: "Claim/copy pre-defined templates to instantly style stores & pages.",
      bullets: [
        "Hero + header themes",
        "Seasonal store layouts",
        "Brand kits (type, spacing, buttons)",
        "Full page templates",
      ],
      tag: "Coming soon",
    },
    {
      title: "Blocks & Widgets",
      icon: <WidgetsIcon />,
      subtitle: "Drop-in widgets for internal dashboards and store experiences.",
      bullets: [
        "KPI tiles + quick actions",
        "Feed modules + activity blocks",
        "Role-based components",
        "Reusable UI sections",
      ],
      tag: "Coming soon",
    },
  ];

  // Quick stats
  const statCards = useMemo(() => {
    const globalCount = updates.filter((u) => u.instanceId == null).length;
    const instanceCount = updates.filter((u) => u.instanceId != null).length;
    const annCount = announcements?.length || 0;

    return [
      { label: "Feed items loaded", value: updates.length, icon: <AutoAwesomeIcon fontSize="small" /> },
      { label: "Global posts", value: globalCount, icon: <PublicIcon fontSize="small" /> },
      { label: "Instance posts", value: instanceCount, icon: <ApartmentIcon fontSize="small" /> },
      { label: "Announcements", value: annCount, icon: <CampaignIcon fontSize="small" /> },
    ];
  }, [updates, announcements]);

  // --- PromoWorld Hero (Top landing area) ---
  const heroAnnouncements = useMemo(() => {
    const items = Array.isArray(announcements) ? announcements : [];
    return items.slice(0, 2); // right side two cards
  }, [announcements]);

  const formatShortDate = (d) => {
    try {
      if (!d) return "";
      return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
    } catch {
      return "";
    }
  };

  const openLinkSafe = (url) => {
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (nonLoginLoading) return null;

  return (
    <div className="promo-world-home full-width-page">

      {/* ✅ Alive background layer */}
      <div className="pw-ambient" aria-hidden="true" />

      <ToastContainer />

      <div className="page-inner-contents">
        <div className="content">
          {/* HERO */}
          <div className="pw-surface pw-hero">
            <div className="pw-shimmer" aria-hidden="true" />

            <div className="pw-hero-row">
              <div className="pw-hero-left">
                <div className="pw-hero-title-row">
                  <div className="pw-icon-badge pw-icon-badge--hero">
                    <StorefrontIcon />
                  </div>

                  <div>
                    <h1 className="pw-title">
                      PromoWorld <span className="pw-title-muted">• Internal</span>
                    </h1>
                    <div className="pw-subtitle">
                      Elite Core v{latestVersion} • Your internal hub for feed, updates, guides, and marketplace tools
                    </div>
                  </div>
                </div>

                <div className="pw-chip-row">
                  <Chip size="small" className="pw-chip" icon={<LockClockIcon />} label="Internal only" />
                  <Chip size="small" className="pw-chip" icon={<TrendingUpIcon />} label="Marketplace coming soon" />
                  <Chip size="small" className="pw-chip" icon={<UpdateIcon />} label="Release notes inside" />
                </div>
              </div>

              {/* Quick stats */}
              <div className="pw-stats-grid">
                {statCards.map((s, idx) => (
                  <div key={idx} className="pw-stat">
                    <div className="pw-stat-top">
                      <div className="pw-stat-label">{s.label}</div>
                      <div className="pw-stat-icon">{s.icon}</div>
                    </div>
                    <div className="pw-stat-value">{s.value}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* TABS */}
          <div className="pw-surface pw-tabs-surface">
            <div className="pw-tabs-wrap">
              <Tabs
                value={tab}
                onChange={(e, v) => setTab(v)}
                //variant="scrollable"
                scrollButtons="auto"
                TabIndicatorProps={{ className: "pw-tab-indicator" }}
                className="pw-tabs"
              >
                <Tab className="pw-tab pw-tab--feed" icon={<AutoAwesomeIcon />} iconPosition="start" label="Feed" />
                <Tab className="pw-tab pw-tab--guides" icon={<MenuBookIcon />} iconPosition="start" label="Guides" />
                <Tab className="pw-tab pw-tab--announcements" icon={<CampaignIcon />} iconPosition="start" label="Announcements" />
                <Tab className="pw-tab pw-tab--updates" icon={<UpdateIcon />} iconPosition="start" label="Update Log" />
                <Tab className="pw-tab pw-tab--marketplace" icon={<StorefrontIcon />} iconPosition="start" label="Marketplace" />
              </Tabs>
            </div>

            <div className="pw-tab-body">
              {/* TAB 0 — FEED */}
              {tab === 0 && (
                <section aria-labelledby="promoworld-feed-heading">

                  {/* PROMOWORLD HERO (Landing) */}
                  <div className="pw-landing-hero">
                    {/* LEFT big hero */}
                    <div className="pw-landing-main">
                      
                      <div className="pw-shimmer soft" aria-hidden="true" />

                      <div className="pw-landing-main-inner">
                        
                        <div class="pw-overlay"></div>
                        <div className="pw-landing-kicker">PromoWorld</div>

                        <div className="pw-landing-title">
                          Your operational hub for <span className="pw-landing-accent">updates</span>,{" "}
                          <span className="pw-landing-accent">guides</span>, and{" "}
                          <span className="pw-landing-accent">marketplace tools</span>
                        </div>

                        {/*<div className="pw-landing-sub">
                          Post company-wide announcements, share instance-specific updates, and keep every team aligned — all in one place.
                        </div>*/}

                        <div className="pw-landing-cta-row">
                          <button
                            type="button"
                            className="pw-landing-cta"
                            onClick={() => setTab(1)}
                          >
                            <MenuBookIcon fontSize="small" />
                            Open Guides
                          </button>

                          <button
                            type="button"
                            className="pw-landing-cta secondary"
                            onClick={() => setTab(3)}
                          >
                            <UpdateIcon fontSize="small" />
                            View Update Log
                          </button>
                        </div>

                        {/*<div className="pw-landing-meta">
                          <span className="pw-landing-pill">
                            <TrendingUpIcon fontSize="small" /> Marketplace coming soon
                          </span>
                          <span className="pw-landing-pill">
                            <UpdateIcon fontSize="small" /> Latest: v{latestVersion}
                          </span>
                        </div>*/}
                      </div>
                    </div>

                    {/* RIGHT mini news stack */}
                    <div className="pw-landing-side">
                      <div className="pw-landing-news-head">
                        <div className="pw-landing-news-title">
                          <CampaignIcon fontSize="small" /> Latest from Elite Core
                        </div>

                        <button
                          type="button"
                          className="pw-landing-news-link"
                          onClick={() => openLinkSafe("https://elitecore.ca/category/announcements/")}
                        >
                          View all →
                        </button>
                      </div>

                      <div className="pw-landing-news-stack">
                        {announcementsLoading ? (
                          <>
                            <div className="pw-landing-news-card is-skeleton" />
                            <div className="pw-landing-news-card is-skeleton" />
                          </>
                        ) : heroAnnouncements.length ? (
                          heroAnnouncements.map((a, i) => (
                            <div
                              key={`${a.link || "ann"}-${i}`}
                              className="pw-landing-news-card"
                              role="button"
                              tabIndex={0}
                              onClick={() => openLinkSafe(a.link)}
                              onKeyDown={(e) => (e.key === "Enter" ? openLinkSafe(a.link) : null)}
                            >
                              <div className="pw-landing-news-glow" aria-hidden="true" />
                              {a.imageUrl ? (
                                <div className="pw-landing-news-img-wrap">
                                  <img className="pw-landing-news-img" src={a.imageUrl} alt="" loading="lazy" />
                                </div>
                              ) : (
                                <div className="pw-landing-news-img-fallback">
                                  <CampaignIcon />
                                </div>
                              )}

                              <div className="pw-landing-news-body">
                                <div className="pw-landing-news-card-title">{a.title || "Announcement"}</div>
                                <div className="pw-landing-news-card-sub">
                                  {a.pubDate ? formatShortDate(a.pubDate) : "New"}
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <>
                            <div className="pw-landing-news-card empty">
                              <div className="pw-landing-news-body">
                                <div className="pw-landing-news-card-title">No announcements yet</div>
                                <div className="pw-landing-news-card-sub">Check back soon.</div>
                              </div>
                            </div>

                            <div
                              className="pw-landing-news-card empty"
                              role="button"
                              tabIndex={0}
                              onClick={() => setTab(3)}
                              onKeyDown={(e) => (e.key === "Enter" ? setTab(3) : null)}
                            >
                              <div className="pw-landing-news-body">
                                <div className="pw-landing-news-card-title">See what shipped recently</div>
                                <div className="pw-landing-news-card-sub">Open Update Log →</div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="pw-section-head">
                    <div>
                      <h2 id="promoworld-feed-heading" className="pw-h2">
                        PromoWorld Feed
                      </h2>
                      <div className="pw-helptext">
                        Global posts are visible across all instances. Instance posts stay scoped.
                      </div>
                    </div>

                    <div className="pw-feed-controls">
                      <button
                        onClick={() => setFeedMode("global")}
                        className={`pw-pill ${feedMode === "global" ? "is-active" : ""}`}
                        type="button"
                      >
                        <PublicIcon fontSize="small" /> Global
                      </button>

                      <button
                        onClick={() => setFeedMode("instance")}
                        className={`pw-pill ${feedMode === "instance" ? "is-active" : ""}`}
                        type="button"
                      >
                        <ApartmentIcon fontSize="small" /> Instance
                      </button>

                      <div className="pw-instance-picker">
                        <span className="pw-instance-label">Instance:</span>
                        <select
                          value={selectedInstanceId}
                          onChange={(e) => setSelectedInstanceId(e.target.value)}
                          className="pw-select"
                        >
                          {instanceOptions.length === 0 ? (
                            <option value="">No instances</option>
                          ) : (
                            instanceOptions.map((opt) => (
                              <option key={opt.id} value={String(opt.id)}>
                                {opt.name}
                              </option>
                            ))
                          )}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Composer */}
                  <div className="pw-surface pw-composer">
                    <div className="pw-shimmer soft" aria-hidden="true" />

                    <div className="pw-composer-head">
                      <h3 className="pw-h3">
                        <MessageIcon fontSize="small" /> Share an update
                      </h3>

                      <div className="pw-posting-to">
                        Posting to:{" "}
                        <span className="pw-posting-to-accent">
                          {feedMode === "global" ? "PromoWorld (Global)" : "Selected Instance"}
                        </span>
                      </div>
                    </div>

                    <div className="pw-composer-form">
                      <TextField
                        className="pw-field"
                        label="Title"
                        variant="filled"
                        fullWidth
                        value={statusTitle}
                        onChange={(e) => setStatusTitle(e.target.value)}
                      />

                      <TextField
                        className="pw-field"
                        label="Message"
                        variant="filled"
                        multiline
                        rows={4}
                        fullWidth
                        value={newStatus}
                        onChange={(e) => setNewStatus(e.target.value)}
                      />

                      <div className="pw-composer-actions">
                        <Button variant="contained" onClick={handleCreatePost} className="pw-primary">
                          Post
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Feed list */}
                  {feedLoading ? (
                    <div className="pw-muted">Loading feed…</div>
                  ) : !updates.length ? (
                    <div className="pw-muted">
                      {feedMode === "global" ? "No global posts yet." : "No posts yet for this instance."}
                    </div>
                  ) : (
                    <div className="pw-post-grid">
                      {updates.map((update) => {
                        const isOpen = !!openComments[update.id];
                        return (
                          <div key={update.id} className="pw-post">
                            <div className="pw-post-glow" aria-hidden="true" />

                            <div className="pw-post-top">
                              <div className="pw-post-author">
                                <img className="pw-avatar" src={getUserAvatar(update.user)} alt="User Avatar" />
                                <div className="pw-post-meta">
                                  <div className="pw-post-title">{update.title || "Post"}</div>
                                  <div className="pw-post-sub">
                                    {formatDistanceToNow(new Date(update.createdAt), { addSuffix: true })}
                                    {" • "}
                                    {update.user?.first_name} {update.user?.last_name}
                                    {" • "}
                                    <span className="pw-scope">
                                      {update.instanceId == null ? "Global" : "Instance"}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {update.tag ? <span className="pw-tag">{update.tag}</span> : null}
                            </div>

                            <div className="pw-post-message">{update.message}</div>

                            <div className="pw-post-actions">
                              <div className="pw-post-actions-left">
                                <Tooltip title="Comments" arrow>
                                  <div
                                    className="pw-inline-action"
                                    onClick={() => toggleComments(update.id)}
                                    role="button"
                                    tabIndex={0}
                                  >
                                    <MessageCircle size={20} />
                                    <span className="pw-count">{(update.comments || []).length}</span>
                                  </div>
                                </Tooltip>

                                <ReactionButtons update={update} accessToken={accessToken} />
                              </div>

                              <Tooltip title={isOpen ? "Hide comments" : "Show comments"} arrow>
                                <button onClick={() => toggleComments(update.id)} className="pw-pill" type="button">
                                  <ArrowForwardIcon className={`pw-arrow ${isOpen ? "is-open" : ""}`} />
                                  {isOpen ? "Hide" : "Open"}
                                </button>
                              </Tooltip>
                            </div>

                            <Collapse in={isOpen} className="pw-comments-collapse">
                              {/*
                              # REMOVED AS PART OF 19.2 UPGRADE
                        
                              <CommentSection
                                currentUser={{
                                  currentUserId: (userDetails?.id || "0").toString(),
                                  currentUserImg: avatarUrlForCommentSection,
                                  currentUserFullName: userDetails?.first_name || "User",
                                }}
                                commentData={(update.comments || [])
                                  .slice()
                                  .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                                  .map((comment) => ({
                                    userId: comment.user?.id?.toString() || "unknown",
                                    comId: comment.id?.toString() || "unknown",
                                    fullName: `${comment.user?.first_name || ""} ${comment.user?.last_name || ""}`.trim(),
                                    text: comment.comment,
                                    avatarUrl: comment.user?.avatarUrl || generateAvatarUrl(comment.user?.first_name || "User"),
                                    timestamp: new Date(comment.createdAt).toISOString(),
                                    replies: [],
                                  }))}
                                onSubmitAction={(data) => handleCommentSubmit(data, update.id)}
                              />*/}
                            </Collapse>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>
              )}

              {/* TAB 1 — GUIDES */}
              {tab === 1 && (
                <section aria-labelledby="guides-heading">
                  <div className="pw-section-head">
                    <div>
                      <h2 id="guides-heading" className="pw-h2">
                        User role guides
                      </h2>
                      <div className="pw-helptext">Fast onboarding — pick a role and open the guide.</div>
                    </div>

                    <div className="pw-chip-row">
                      <Chip size="small" icon={<MenuBookIcon />} label="Docs" className="pw-chip" />
                      <Chip size="small" icon={<AutoAwesomeIcon />} label="More guides coming" className="pw-chip" />
                    </div>
                  </div>

                  <div className="role-grid-fallback">
                    {roleGuides.map((g) => (
                      <a
                        key={g.label}
                        href={g.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="pw-cardlink"
                      >
                        <div className="pw-cardlink-glow" aria-hidden="true" />
                        <div className="pw-icon-badge pw-icon-badge--card">{g.icon}</div>

                        <div className="pw-cardlink-text">
                          <div className="pw-cardlink-title">{g.label}</div>
                          <div className="pw-cardlink-sub">Open guide →</div>
                        </div>
                      </a>
                    ))}
                  </div>
                </section>
              )}

              {/* TAB 2 — ANNOUNCEMENTS */}
              {tab === 2 && (
                <section aria-labelledby="announcements-heading">
                  <div className="pw-section-head">
                    <div>
                      <h2 id="announcements-heading" className="pw-h2">
                        Announcements
                      </h2>
                      <div className="pw-helptext">Latest news from Elite Core — top posts only.</div>
                    </div>

                    <a
                      href="https://elitecore.ca/category/announcements/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="pw-link"
                    >
                      View all →
                    </a>
                  </div>

                  <div className="pw-ann-wrap">
                    {announcementsLoading ? (
                      <div className="pw-muted">Loading announcements…</div>
                    ) : announcementsError ? (
                      <div className="pw-muted">{announcementsError}</div>
                    ) : !announcements.length ? (
                      <div className="pw-muted">No announcements right now.</div>
                    ) : (
                      <div className="pw-ann-grid">
                        {announcements.slice(0, 3).map((a, i) => (
                          <a
                            key={`${a.link || "announcement"}-${i}`}
                            href={a.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="pw-ann-card"
                          >
                            <div className="pw-cardlink-glow" aria-hidden="true" />

                            {a.imageUrl ? <img className="pw-ann-img" src={a.imageUrl} alt="" loading="lazy" /> : null}

                            <div className="pw-ann-title">{a.title || "Announcement"}</div>

                            {a.pubDate ? <div className="pw-ann-date">{new Date(a.pubDate).toLocaleDateString()}</div> : null}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </section>
              )}

              {/* TAB 3 — UPDATE LOG */}
              {tab === 3 && (
                <section aria-labelledby="release-notes-heading">
                  <div className="pw-section-head">
                    <div>
                      <h2 id="release-notes-heading" className="pw-h2">
                        Update Log
                      </h2>
                      <div className="pw-helptext">Latest development log entries from Elite Core.</div>
                    </div>

                    <div className="pw-chip-row">
                      <Chip size="small" icon={<UpdateIcon />} label={`Latest: v${latestVersion}`} className="pw-chip" />
                      <a
                        href="https://elitecore.ca/category/elite-core-development-log/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="pw-link"
                      >
                        View all →
                      </a>
                    </div>
                  </div>

                  <div className="pw-log-wrap">
                    {developmentLogLoading ? (
                      <div className="pw-muted">Loading update log…</div>
                    ) : developmentLogError ? (
                      <div className="pw-muted">{developmentLogError}</div>
                    ) : !developmentLog.length ? (
                      <div className="pw-muted">No update log entries right now.</div>
                    ) : (
                      <>
                        <div className="pw-update-grid">
                          {developmentLogPageItems.map((entry, idx) => (
                            <a
                              key={`${entry.link || "update"}-${idx}`}
                              href={entry.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="pw-update-card"
                            >
                              <div className="pw-cardlink-glow" aria-hidden="true" />
                              {entry.imageUrl ? (
                                <img className="pw-update-img" src={entry.imageUrl} alt="" loading="lazy" />
                              ) : (
                                <div className="pw-update-fallback">
                                  <UpdateIcon fontSize="small" />
                                </div>
                              )}

                              <div className="pw-update-body">
                                <div className="pw-update-title">{entry.title || "Update"}</div>
                                {entry.pubDate ? (
                                  <div className="pw-update-date">{formatShortDate(entry.pubDate)}</div>
                                ) : null}
                                {entry.excerpt ? <div className="pw-update-excerpt">{entry.excerpt}</div> : null}
                              </div>
                            </a>
                          ))}
                        </div>

                        {developmentLogPageCount > 1 ? (
                          <div className="pw-log-pagination">
                            <button
                              type="button"
                              className="pw-pill"
                              onClick={() => setDevelopmentLogPage((prev) => Math.max(prev - 1, 0))}
                              disabled={developmentLogPage === 0}
                            >
                              Previous
                            </button>
                            <span className="pw-log-page">
                              Page {developmentLogPage + 1} of {developmentLogPageCount}
                            </span>
                            <button
                              type="button"
                              className="pw-pill"
                              onClick={() => setDevelopmentLogPage((prev) => Math.min(prev + 1, developmentLogPageCount - 1))}
                              disabled={developmentLogPage === developmentLogPageCount - 1}
                            >
                              Next
                            </button>
                          </div>
                        ) : null}
                      </>
                    )}
                  </div>

                  <div className="pw-surface pw-coming">
                    <div className="pw-shimmer soft" aria-hidden="true" />
                    <h3 className="pw-h3 pw-coming-title">
                      <AutoAwesomeIcon fontSize="small" /> Coming Soon
                    </h3>
                    <p className="pw-coming-sub">A quick peek at what’s next (thanks for all the feedback)!</p>
                    <ul className="pw-coming-list">
                      {upcoming.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  </div>
                </section>
              )}

              {/* TAB 4 — MARKETPLACE */}
              {tab === 4 && (
                <section aria-labelledby="marketplace-heading">
                  <div className="pw-section-head">
                    <div>
                      <h2 id="marketplace-heading" className="pw-h2">
                        Marketplace <span className="pw-h2-muted">(Coming Soon)</span>
                      </h2>
                      <div className="pw-helptext">Add-ons, designs, and internal widgets — installable per instance.</div>
                    </div>

                    <Chip size="small" icon={<StorefrontIcon />} label="Roadmap preview" className="pw-chip" />
                  </div>

                  <div className="pw-market-grid">
                    {marketplaceCards.map((c, idx) => (
                      <div key={idx} className="pw-market-card">
                        <div className="pw-shimmer soft" aria-hidden="true" />

                        <div className="pw-market-top">
                          <div className="pw-market-left">
                            <div className="pw-icon-badge pw-icon-badge--market">{c.icon}</div>
                            <div>
                              <div className="pw-market-title">{c.title}</div>
                              <div className="pw-market-sub">{c.subtitle}</div>
                            </div>
                          </div>

                          <span className="pw-tag">{c.tag}</span>
                        </div>

                        <ul className="pw-market-bullets">
                          {c.bullets.map((b, i) => (
                            <li key={i}>{b}</li>
                          ))}
                        </ul>

                        <div className="pw-market-actions">
                          <Button variant="outlined" disabled className="pw-install-soon">
                            Install (soon)
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="pw-surface pw-buckets">
                    <h3 className="pw-h3">
                      <TrendingUpIcon fontSize="small" /> Suggested Marketplace Buckets
                    </h3>
                    <div className="pw-helptext">When you’re ready, the marketplace can be split into installable bundles:</div>

                    <div className="pw-buckets-grid">
                      {[
                        { title: "Store Add-ons", desc: "Checkout guards, closing dates, domain restrictions, vouchers." },
                        { title: "Design Packs", desc: "Hero templates, category layouts, typography & spacing presets." },
                        { title: "Internal Widgets", desc: "Dashboards, feeds, quick actions, role toolkits." },
                      ].map((x, i) => (
                        <div key={i} className="pw-mini">
                          <div className="pw-mini-title">{x.title}</div>
                          <div className="pw-mini-desc">{x.desc}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>
              )}
            </div>
          </div>

          <div className="pw-spacer" />
        </div>
      </div>
    </div>
  );
}

export default WelcomeToEliteCore;
